<template>
  <div class="active-session">

    <!-- ── Sticky header: name, timer, live calories ── -->
    <div class="session-bar">
      <div class="session-bar-top">
        <input v-if="editingName" ref="nameInput" v-model="nameDraft" class="name-edit"
          @keyup.enter="saveName" @blur="saveName" />
        <button v-else class="session-name" @click="startEditName" title="Byt namn">
          {{ session.name }} <span class="edit-pencil">✏</span>
        </button>
        <button class="btn-icon-ghost" :class="{ active: showSessionNote }" @click="toggleSessionNote" title="Anteckning för passet">📝</button>
        <button class="btn-discard" @click="discard" title="Avbryt pass">✕</button>
      </div>
      <div class="session-bar-stats">
        <div class="stat timer"><span class="stat-icon">⏱</span><span class="stat-val">{{ formatDuration(elapsedSeconds) }}</span></div>
        <div class="stat"><span class="stat-icon">🔥</span><span class="stat-val">{{ liveCalories != null ? Math.round(liveCalories) + ' kcal' : '–' }}</span></div>
        <div class="stat"><span class="stat-icon">✅</span><span class="stat-val">{{ doneSets }}/{{ totalSets }} set</span></div>
      </div>
      <transition name="expand">
        <textarea v-if="showSessionNote" v-model="session.notes" class="note-area" rows="2"
          placeholder="Anteckning för passet…" @blur="saveSessionNote"></textarea>
      </transition>
      <button class="btn-finish" @click="openComplete">Avsluta pass</button>
    </div>

    <!-- ── Rest timer ── -->
    <transition name="slide">
      <div v-if="rest.active" class="rest-timer" :class="{ done: restRemaining <= 0 }">
        <span class="rest-label">Vila</span>
        <span class="rest-time">{{ formatDuration(restRemaining) }}</span>
        <div class="rest-actions">
          <button @click="addRest(-15)">-15s</button>
          <button @click="addRest(15)">+15s</button>
          <button class="rest-skip" @click="stopRest">Klar</button>
        </div>
      </div>
    </transition>

    <transition name="pop">
      <div v-if="prMessage" class="pr-toast">🏆 {{ prMessage }}</div>
    </transition>

    <div v-if="exercises.length === 0" class="empty-hint">
      Inga övningar än. Lägg till din första övning nedan.
    </div>

    <!-- ── Exercises ── -->
    <div v-for="ex in exercises" :key="ex.id" class="exercise-card">
      <div class="ex-head">
        <div class="ex-title">
          <strong>{{ ex.exerciseType.name }}</strong>
          <span class="ex-tags">
            <span v-for="tag in capabilityTags(ex.exerciseType)" :key="tag" class="ex-tag">{{ tag }}</span>
          </span>
        </div>
        <button class="btn-icon-ghost" :class="{ active: noteOpen[ex.id] }" @click="toggleExNote(ex)" title="Anteckning för övningen">📝</button>
        <button class="btn-ex-remove" @click="removeExercise(ex)" title="Ta bort övning">🗑</button>
      </div>

      <transition name="expand">
        <textarea v-if="noteOpen[ex.id]" v-model="ex.notes" class="note-area" rows="2"
          placeholder="Anteckning för övningen…" @blur="saveExNote(ex)"></textarea>
      </transition>

      <div v-if="lastPerf[ex.exerciseTypeId]" class="last-perf">
        Förra ({{ formatDate(lastPerf[ex.exerciseTypeId].date) }}):
        <span class="last-perf-sets">{{ lastPerfSummary(ex) }}</span>
      </div>

      <ul class="set-list">
        <li v-for="set in orderedSets(ex)" :key="set.id"
            class="set-item" :class="{ done: set.isCompleted, warmup: set.isWarmup }">

          <!-- Completed set: compact, tap edit-pencil to reopen -->
          <template v-if="set.isCompleted">
            <span class="set-idx">{{ set.isWarmup ? 'V' : workingIndex(ex, set) }}</span>
            <div class="set-done-body">
              <span class="set-summary">{{ setSummary(set, ex.exerciseType) }}
                <span v-if="set.status === 'competition'" class="set-flag-mark" title="Tävlingslyft">🎖️</span>
                <span v-else-if="set.status === 'excluded'" class="set-flag-mark" :title="set.statusNote || 'Räknas ej'">⛔</span>
              </span>
              <span v-if="set.notes" class="set-note-view">📝 {{ set.notes }}</span>
            </div>
            <button class="set-flag" @click="openSetStatus(ex, set)" title="Rekordstatus (träning/tävling/räknas ej)">🏅</button>
            <button class="set-reopen" @click="reopenSet(ex, set)" title="Ångra / redigera">↺</button>
            <button class="set-del" @click="removeSet(ex, set)">✕</button>
          </template>

          <!-- Planned (open) set: editable + Klar -->
          <template v-else>
            <div class="planned-set">
              <div class="planned-top">
                <span class="set-idx open">{{ set.isWarmup ? 'V' : workingIndex(ex, set) }}</span>
                <div class="planned-fields">
                  <label v-if="ex.exerciseType.tracksReps" class="pf"><span>Reps</span>
                    <input type="number" inputmode="numeric" v-model.number="set.reps" /></label>
                  <label v-if="ex.exerciseType.tracksWeight" class="pf"><span>{{ ex.exerciseType.isBodyweight ? '+Vikt' : 'Vikt' }}</span>
                    <input type="number" inputmode="decimal" step="0.5" v-model.number="set.weight" /></label>
                  <label v-if="ex.exerciseType.tracksDistance" class="pf"><span>Distans (m)</span>
                    <input type="number" inputmode="decimal" v-model.number="set.distanceMeters" /></label>
                  <label v-if="ex.exerciseType.tracksDuration" class="pf pf-time"><span>Tid (s)</span>
                    <div class="time-row">
                      <input type="number" inputmode="numeric" v-model.number="set.durationSeconds" />
                      <button type="button" class="sw-btn" :class="{ running: isStopwatchRunning(set.id) }"
                        @click="toggleStopwatch(set)">{{ isStopwatchRunning(set.id) ? '⏹ ' + formatDuration(stopwatchElapsed(set.id)) : '⏱' }}</button>
                    </div>
                  </label>
                  <label class="pf pf-sm"><span>RPE</span>
                    <input type="number" inputmode="decimal" step="0.5" min="1" max="10" v-model.number="set.rpe" /></label>
                </div>
              </div>
              <div class="planned-actions">
                <label class="warmup-toggle"><input type="checkbox" v-model="set.isWarmup" /> Uppv.</label>
                <button class="set-note-btn" :class="{ active: set.notes }" @click="toggleSetNote(set)" title="Notering">📝</button>
                <button class="set-del-open" @click="removeSet(ex, set)" title="Ta bort set">🗑</button>
                <button class="btn-done" @click="completeSet(ex, set)">✓ Klar</button>
              </div>
              <transition name="expand">
                <input v-if="setNoteOpen[set.id]" v-model="set.notes" class="set-note-input"
                  placeholder="Notering för setet…" />
              </transition>
            </div>
          </template>
        </li>
      </ul>

      <button class="btn-add-set" @click="addPlannedSet(ex)">+ Lägg till set</button>
    </div>

    <!-- ── Add exercise ── -->
    <div class="add-exercise">
      <div v-if="!picker.open" class="add-exercise-bar">
        <button class="btn-add-exercise" @click="openPicker">+ Lägg till övning</button>
      </div>
      <div v-else class="exercise-picker">
        <input ref="pickerInput" v-model="picker.query" class="picker-search" placeholder="Sök övning..." @keyup.escape="picker.open = false" />
        <div class="picker-list">
          <button v-for="et in filteredExerciseTypes" :key="et.id" class="picker-item" @click="pickExercise(et)">
            <span>{{ et.name }}</span>
            <span class="picker-tags">{{ capabilityTags(et).join(' · ') }}</span>
          </button>
          <div v-if="filteredExerciseTypes.length === 0" class="picker-empty">Inga övningar matchar. Skapa nya under fliken Grunddata.</div>
        </div>
        <button class="picker-close" @click="picker.open = false">Stäng</button>
      </div>
    </div>

    <CompleteSessionModal v-if="completeOpen" :session="session" :exercises="exercises"
      :body-weight-kg="bodyWeightKg" :elapsed-seconds="elapsedSeconds"
      @confirm="onCompleteConfirm" @cancel="completeOpen = false" />
    <LiftStatusSheet v-if="statusSheet" :context="statusSheet" @close="statusSheet = null" @set-updated="onSetStatusUpdated" />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { gymAuthState, refreshIfNeeded } from '../../services/gymTrackerAuth.js';
import api from '../../services/gymTrackerApi.js';
import { sessionStore, clearActiveSession } from '../../services/gymSessionStore.js';
import { formatDuration, computeCalories, setSummary, epley1rm, numOrNull } from '../../services/gymHelpers.js';
import CompleteSessionModal from './CompleteSessionModal.vue';
import LiftStatusSheet from './LiftStatusSheet.vue';

const REST_DEFAULT = 90;

export default {
  name: 'ActiveSession',
  components: { CompleteSessionModal, LiftStatusSheet },
  props: {
    exerciseTypes: { type: Array, default: () => [] },
    bodyWeightKg: { type: Number, default: null },
  },
  emits: ['completed', 'discarded', 'toast'],
  setup(props, { emit }) {
    const session = computed(() => sessionStore.active);
    const exercises = computed(() =>
      [...(session.value?.gymSessionExercises || [])].sort((a, b) => a.order - b.order));

    async function withToken(fn) { await refreshIfNeeded(); return fn(gymAuthState.token); }
    function toast(msg, error = false) { emit('toast', { msg, error }); }

    // ── Ticking clock ──
    const nowMs = ref(Date.now());
    let ticker = null;
    const startMs = computed(() => {
      const t = session.value?.startTime;
      return t ? new Date(t).getTime() : nowMs.value;
    });
    const elapsedSeconds = computed(() => Math.max(0, Math.floor((nowMs.value - startMs.value) / 1000)));

    const allSets = computed(() => exercises.value.flatMap(ex => ex.sets));
    const totalSets = computed(() => allSets.value.length);
    const doneSets = computed(() => allSets.value.filter(s => s.isCompleted).length);

    const liveCalories = computed(() => {
      if (!props.bodyWeightKg) return null;
      return computeCalories(exercises.value, props.bodyWeightKg, elapsedSeconds.value, {}).total;
    });

    // ── Rest timer ──
    const rest = reactive({ active: false, endsAt: 0 });
    const restRemaining = computed(() => rest.active ? Math.max(0, Math.ceil((rest.endsAt - nowMs.value) / 1000)) : 0);
    function startRest(seconds = REST_DEFAULT) { rest.active = true; rest.endsAt = Date.now() + seconds * 1000; }
    function addRest(delta) { rest.endsAt = Math.max(Date.now(), rest.endsAt + delta * 1000); }
    function stopRest() { rest.active = false; }
    let restWasPositive = false;
    function checkRestDone() {
      if (rest.active && restRemaining.value > 0) restWasPositive = true;
      if (rest.active && restRemaining.value <= 0 && restWasPositive) {
        restWasPositive = false;
        if (navigator.vibrate) navigator.vibrate(200);
      }
    }

    onMounted(() => {
      ticker = setInterval(() => { nowMs.value = Date.now(); checkRestDone(); }, 1000);
      loadPersonalBests();
      exercises.value.forEach(ex => ensureLastPerf(ex.exerciseTypeId));
    });
    onBeforeUnmount(() => { if (ticker) clearInterval(ticker); });

    function orderedSets(ex) { return [...ex.sets].sort((a, b) => a.order - b.order); }
    function workingIndex(ex, set) {
      let n = 0;
      for (const s of orderedSets(ex)) { if (!s.isWarmup) { n++; if (s.id === set.id) return n; } }
      return n;
    }

    // ── Stopwatch (per set) ──
    const stopwatches = reactive({});
    function isStopwatchRunning(setId) { return !!stopwatches[setId]?.running; }
    function stopwatchElapsed(setId) {
      const sw = stopwatches[setId];
      return sw?.running ? Math.floor((nowMs.value - sw.startedAt) / 1000) : 0;
    }
    function toggleStopwatch(set) {
      const sw = stopwatches[set.id];
      if (sw?.running) {
        set.durationSeconds = Math.max(1, Math.round((Date.now() - sw.startedAt) / 1000));
        stopwatches[set.id] = { running: false };
      } else {
        stopwatches[set.id] = { running: true, startedAt: Date.now() };
      }
    }

    // ── Personal bests (PR celebration) ──
    const bestE1rm = reactive({});
    const prMessage = ref('');
    let prTimer = null;
    async function loadPersonalBests() {
      try {
        const d = await withToken(t => api.getPersonalBests(t));
        (d.personalBests || []).forEach(pb => {
          bestE1rm[pb.exerciseTypeId] = Math.max(0, ...pb.repRecords.map(r => epley1rm(r.weight, r.reps)));
        });
      } catch (e) { /* non-fatal */ }
    }
    function celebrate(msg) {
      prMessage.value = msg;
      if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
      clearTimeout(prTimer);
      prTimer = setTimeout(() => { prMessage.value = ''; }, 4000);
    }
    function checkPr(ex, set) {
      if (set.isWarmup || !set.weight || !set.reps) return;
      const e1 = epley1rm(set.weight, set.reps);
      const prev = bestE1rm[ex.exerciseTypeId] || 0;
      if (e1 > prev + 0.01) {
        bestE1rm[ex.exerciseTypeId] = e1;
        if (prev > 0) celebrate(`Nytt PB i ${ex.exerciseType.name}! ${set.weight} kg × ${set.reps}`);
      }
    }

    // ── "Last time" reference ──
    const lastPerf = reactive({});
    async function ensureLastPerf(exTypeId) {
      if (lastPerf[exTypeId] !== undefined) return;
      lastPerf[exTypeId] = null;
      try {
        const d = await withToken(t => api.getLastExercisePerformance(exTypeId, session.value?.id, t));
        if (d.lastExercisePerformance) lastPerf[exTypeId] = d.lastExercisePerformance;
      } catch (e) { /* ignore */ }
    }
    function lastPerfSummary(ex) {
      const lp = lastPerf[ex.exerciseTypeId];
      if (!lp) return '';
      return lp.sets.filter(s => !s.isWarmup).map(s => setSummary(s, ex.exerciseType)).join(', ');
    }

    // ── Set fields payload (sanitised) ──
    function setPayload(set, extra = {}) {
      return {
        reps: numOrNull(set.reps), weight: numOrNull(set.weight),
        distanceMeters: numOrNull(set.distanceMeters), durationSeconds: numOrNull(set.durationSeconds),
        rpe: numOrNull(set.rpe), isWarmup: !!set.isWarmup, notes: set.notes || null, ...extra,
      };
    }

    // ── Mark a planned set done ──
    async function completeSet(ex, set) {
      const hasData = [set.reps, set.weight, set.distanceMeters, set.durationSeconds]
        .some(v => v != null && v !== '');
      if (!hasData) { toast('Fyll i minst ett värde för setet.', true); return; }
      try {
        const res = await withToken(t => api.updateSet({ id: set.id, ...setPayload(set, { isCompleted: true }) }, t));
        Object.assign(set, res.updateSessionSet);
        checkPr(ex, set);
        if (!set.isWarmup) startRest();
      } catch (e) { toast(`Kunde inte spara set: ${e.message}`, true); }
    }

    async function reopenSet(ex, set) {
      try {
        const res = await withToken(t => api.updateSet({ id: set.id, isCompleted: false }, t));
        Object.assign(set, res.updateSessionSet);
      } catch (e) { toast(`Kunde inte ångra set: ${e.message}`, true); }
    }

    async function addPlannedSet(ex) {
      const last = orderedSets(ex)[ex.sets.length - 1];
      const seed = last
        ? { reps: last.reps, weight: last.weight, distanceMeters: last.distanceMeters, durationSeconds: last.durationSeconds }
        : {};
      try {
        const input = {
          sessionExerciseId: ex.id,
          reps: numOrNull(seed.reps), weight: numOrNull(seed.weight),
          distanceMeters: numOrNull(seed.distanceMeters), durationSeconds: numOrNull(seed.durationSeconds),
          rpe: null, isWarmup: false, isCompleted: false, notes: null,
        };
        const res = await withToken(t => api.addSet(input, t));
        ex.sets.push(res.addSessionSet);
      } catch (e) { toast(`Kunde inte lägga till set: ${e.message}`, true); }
    }

    async function removeSet(ex, set) {
      try {
        await withToken(t => api.removeSet(set.id, t));
        const i = ex.sets.findIndex(s => s.id === set.id);
        if (i !== -1) ex.sets.splice(i, 1);
      } catch (e) { toast(`Kunde inte ta bort set: ${e.message}`, true); }
    }

    async function removeExercise(ex) {
      if (!confirm(`Ta bort ${ex.exerciseType.name} från passet?`)) return;
      try {
        await withToken(t => api.removeExercise(ex.id, t));
        const list = session.value.gymSessionExercises;
        const i = list.findIndex(e => e.id === ex.id);
        if (i !== -1) list.splice(i, 1);
      } catch (e) { toast(`Kunde inte ta bort övning: ${e.message}`, true); }
    }

    // ── Notes ──
    const showSessionNote = ref(false);
    function toggleSessionNote() { showSessionNote.value = !showSessionNote.value; }
    async function saveSessionNote() {
      try { await withToken(t => api.updateSession({ id: session.value.id, notes: session.value.notes || '' }, t)); }
      catch (e) { toast(`Kunde inte spara anteckning: ${e.message}`, true); }
    }
    const noteOpen = reactive({});
    function toggleExNote(ex) { noteOpen[ex.id] = !noteOpen[ex.id]; }
    async function saveExNote(ex) {
      try { await withToken(t => api.updateSessionExercise({ id: ex.id, notes: ex.notes || '' }, t)); }
      catch (e) { toast(`Kunde inte spara anteckning: ${e.message}`, true); }
    }
    const setNoteOpen = reactive({});
    function toggleSetNote(set) { setNoteOpen[set.id] = !setNoteOpen[set.id]; }

    // ── Exercise picker ──
    const picker = reactive({ open: false, query: '' });
    const pickerInput = ref(null);
    function openPicker() { picker.open = true; picker.query = ''; nextTick(() => pickerInput.value?.focus()); }
    const filteredExerciseTypes = computed(() => {
      const q = picker.query.trim().toLowerCase();
      return props.exerciseTypes.filter(et => !q || et.name.toLowerCase().includes(q));
    });
    async function pickExercise(et) {
      try {
        const order = exercises.value.length + 1;
        const res = await withToken(t => api.addExercise({ gymSessionId: session.value.id, exerciseTypeId: et.id, order }, t));
        const newEx = res.addExerciseToSession;
        if (!newEx.sets) newEx.sets = [];
        session.value.gymSessionExercises.push(newEx);
        picker.open = false;
        ensureLastPerf(et.id);
        await addPlannedSet(newEx); // start with one open set to fill
      } catch (e) { toast(`Kunde inte lägga till övning: ${e.message}`, true); }
    }

    // ── Name editing ──
    const editingName = ref(false);
    const nameDraft = ref('');
    const nameInput = ref(null);
    function startEditName() { nameDraft.value = session.value.name; editingName.value = true; nextTick(() => nameInput.value?.focus()); }
    async function saveName() {
      if (!editingName.value) return;
      editingName.value = false;
      const name = nameDraft.value.trim();
      if (!name || name === session.value.name) return;
      try { await withToken(t => api.updateSession({ id: session.value.id, name }, t)); session.value.name = name; }
      catch (e) { toast(`Kunde inte byta namn: ${e.message}`, true); }
    }

    // ── Complete / discard ──
    const completeOpen = ref(false);
    function openComplete() {
      if (doneSets.value === 0 && !confirm('Inga set är markerade som klara. Avsluta ändå?')) return;
      completeOpen.value = true;
    }
    async function onCompleteConfirm(payload) {
      try {
        await withToken(t => api.completeSession({
          id: session.value.id, name: payload.name,
          durationSecondsOverride: payload.durationSecondsOverride, allocations: payload.allocations,
        }, t));
        completeOpen.value = false;
        clearActiveSession();
        toast('Pass sparat! 💪');
        emit('completed');
      } catch (e) { toast(`Kunde inte avsluta passet: ${e.message}`, true); }
    }
    async function discard() {
      if (!confirm('Avbryt och radera detta pågående pass? Detta går inte att ångra.')) return;
      try {
        await withToken(t => api.deleteSession(session.value.id, t));
        clearActiveSession();
        toast('Pass avbrutet.');
        emit('discarded');
      } catch (e) { toast(`Kunde inte avbryta passet: ${e.message}`, true); }
    }

    function capabilityTags(et) {
      const tags = [];
      if (et.isBodyweight) tags.push('Kroppsvikt');
      if (et.tracksWeight) tags.push('Vikt');
      if (et.tracksReps) tags.push('Reps');
      if (et.tracksDistance) tags.push('Distans');
      if (et.tracksDuration) tags.push('Tid');
      return tags;
    }
    function formatDate(d) { return new Date(d).toLocaleDateString('sv-SE'); }

    // Rekordstatus på ett klart set (träning/tävling/räknas ej) – följer med lyftet vid avslut.
    const statusSheet = ref(null);
    function openSetStatus(ex, set) {
      statusSheet.value = { setId: set.id, set, exerciseTypeId: ex.exerciseType.id, exerciseTypeName: ex.exerciseType.name, reps: set.reps };
    }
    function onSetStatusUpdated({ status, statusNote }) {
      const set = statusSheet.value?.set;
      if (set) { set.status = status; set.statusNote = statusNote; }
    }

    return {
      statusSheet, openSetStatus, onSetStatusUpdated,
      session, exercises, totalSets, doneSets, elapsedSeconds, liveCalories,
      rest, restRemaining, addRest, stopRest, prMessage,
      orderedSets, workingIndex,
      stopwatches, isStopwatchRunning, stopwatchElapsed, toggleStopwatch,
      lastPerf, lastPerfSummary,
      completeSet, reopenSet, addPlannedSet, removeSet, removeExercise,
      showSessionNote, toggleSessionNote, saveSessionNote,
      noteOpen, toggleExNote, saveExNote, setNoteOpen, toggleSetNote,
      picker, pickerInput, openPicker, filteredExerciseTypes, pickExercise,
      editingName, nameDraft, nameInput, startEditName, saveName,
      completeOpen, openComplete, onCompleteConfirm, discard,
      capabilityTags, formatDate, formatDuration, setSummary,
    };
  },
};
</script>

<style scoped>
.active-session { display: flex; flex-direction: column; gap: 0.85rem; }

/* Sticky session bar */
.session-bar {
  position: sticky; top: 0.5rem; z-index: 20;
  background: linear-gradient(135deg, var(--gym-accent), var(--gym-accent-active));
  color: var(--gym-on-accent); border-radius: var(--gym-radius-lg); padding: 0.95rem 1rem;
  box-shadow: var(--gym-shadow-accent); display: flex; flex-direction: column; gap: 0.6rem;
}
.session-bar-top { display: flex; align-items: center; gap: 0.4rem; }
.session-name {
  background: none; border: none; color: var(--gym-on-accent); font-size: 1.15rem; font-weight: 700;
  cursor: pointer; padding: 0; text-align: left; display: flex; align-items: center; gap: 0.4rem; margin-right: auto;
}
.edit-pencil { font-size: 0.8rem; opacity: 0.7; }
.name-edit {
  flex: 1; font-size: 1.1rem; font-weight: 700; padding: 0.4rem 0.55rem; margin-right: auto;
  border: none; border-radius: var(--gym-radius-sm); background: var(--gym-surface); color: var(--gym-text);
}
.btn-icon-ghost {
  background: rgba(255,255,255,0.16); border: none; color: var(--gym-on-accent);
  width: 30px; height: 30px; border-radius: 8px; cursor: pointer; font-size: 0.85rem; flex-shrink: 0;
}
.btn-icon-ghost.active { background: rgba(255,255,255,0.34); }
.btn-discard {
  background: rgba(255,255,255,0.2); border: none; color: var(--gym-on-accent);
  width: 30px; height: 30px; border-radius: 50%; cursor: pointer; font-size: 0.85rem; flex-shrink: 0;
}
.btn-discard:hover { background: rgba(255,255,255,0.34); }
.session-bar-stats { display: flex; gap: 1.2rem; }
.stat { display: flex; align-items: center; gap: 0.35rem; font-size: 1rem; }
.stat.timer .stat-val { font-variant-numeric: tabular-nums; font-weight: 700; font-size: 1.15rem; }
.note-area {
  width: 100%; border: none; border-radius: var(--gym-radius-sm); padding: 0.5rem 0.6rem;
  font-size: 0.9rem; font-family: inherit; resize: vertical; background: var(--gym-surface); color: var(--gym-text);
}
.btn-finish {
  background: var(--gym-on-accent); color: var(--gym-accent-active); border: none; border-radius: var(--gym-radius-sm);
  padding: 0.65rem; font-size: 0.95rem; font-weight: 700; cursor: pointer; width: 100%;
}

/* Rest timer */
.rest-timer {
  display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap;
  background: var(--gym-warning-soft); border: 1px solid color-mix(in srgb, var(--gym-warning) 45%, transparent);
  border-radius: var(--gym-radius); padding: 0.55rem 0.85rem;
}
.rest-timer.done { background: var(--gym-success-soft); border-color: color-mix(in srgb, var(--gym-success) 45%, transparent); }
.rest-label { font-weight: 700; color: var(--gym-warning); }
.rest-timer.done .rest-label { color: var(--gym-success); }
.rest-time { font-variant-numeric: tabular-nums; font-weight: 700; font-size: 1.1rem; color: var(--gym-text); }
.rest-actions { display: flex; gap: 0.35rem; margin-left: auto; }
.rest-actions button { background: var(--gym-surface-2); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); padding: 0.35rem 0.65rem; font-size: 0.8rem; cursor: pointer; color: var(--gym-text); }
.rest-skip { font-weight: 700; color: var(--gym-accent) !important; }

/* PR toast */
.pr-toast {
  position: fixed; top: calc(var(--navbar-height, 70px) + 0.75rem); left: 50%; transform: translateX(-50%);
  background: linear-gradient(135deg, #f59e0b, #ef4444); color: #fff;
  padding: 0.7rem 1.2rem; border-radius: var(--gym-radius-pill); font-weight: 700; z-index: 1350;
  box-shadow: 0 10px 28px rgba(239,68,68,0.45); font-size: 0.95rem;
}

.empty-hint { color: var(--gym-text-faint); font-size: 0.9rem; text-align: center; padding: 1.5rem 0; }

/* Exercise card */
.exercise-card {
  background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius);
  padding: 0.9rem; display: flex; flex-direction: column; gap: 0.6rem; box-shadow: var(--gym-shadow-sm);
}
.ex-head { display: flex; align-items: flex-start; gap: 0.5rem; }
.ex-title { display: flex; flex-direction: column; gap: 0.25rem; margin-right: auto; }
.ex-title strong { font-size: 1.02rem; color: var(--gym-text); }
.ex-tags { display: flex; gap: 0.3rem; flex-wrap: wrap; }
.ex-tag { font-size: 0.68rem; background: var(--gym-surface-3); color: var(--gym-text-muted); padding: 0.12rem 0.5rem; border-radius: var(--gym-radius-pill); }
.btn-ex-remove { background: none; border: none; cursor: pointer; font-size: 0.95rem; color: var(--gym-text-faint); }
.btn-ex-remove:hover { color: var(--gym-danger); }

.last-perf { font-size: 0.78rem; color: var(--gym-text-muted); background: var(--gym-surface-2); padding: 0.4rem 0.6rem; border-radius: var(--gym-radius-sm); }
.last-perf-sets { color: var(--gym-text); }

/* Set list */
.set-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.4rem; }
.set-item { border-radius: var(--gym-radius-sm); }

/* Completed set (compact) */
.set-item.done {
  display: flex; align-items: center; gap: 0.6rem;
  background: var(--gym-success-soft); border: 1px solid color-mix(in srgb, var(--gym-success) 30%, transparent);
  padding: 0.45rem 0.6rem; font-size: 0.9rem;
}
.set-idx {
  width: 22px; height: 22px; flex-shrink: 0; border-radius: 50%;
  background: var(--gym-success); color: #06281a; font-size: 0.72rem; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
}
.set-item.done.warmup .set-idx, .set-idx.open.warmup { background: var(--gym-warning); color: #1a1205; }
.set-done-body { flex: 1; display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.set-summary { color: var(--gym-text); }
.set-note-view { font-size: 0.72rem; color: var(--gym-text-muted); }
.set-reopen, .set-del { background: none; border: none; cursor: pointer; font-size: 0.9rem; color: var(--gym-text-muted); padding: 0.15rem 0.3rem; }
.set-reopen:hover { color: var(--gym-accent); }
.set-del:hover { color: var(--gym-danger); }

/* Planned (open) set */
.set-item:not(.done) {
  background: var(--gym-surface-2); border: 1px dashed var(--gym-border-strong); padding: 0.6rem;
}
.set-item:not(.done).warmup { background: var(--gym-warning-soft); }
.planned-set { display: flex; flex-direction: column; gap: 0.5rem; }
.planned-top { display: flex; align-items: flex-start; gap: 0.5rem; }
.set-idx.open { background: var(--gym-accent); color: var(--gym-on-accent); margin-top: 0.9rem; }
.planned-fields { display: flex; flex-wrap: wrap; gap: 0.4rem; flex: 1; }
.pf { display: flex; flex-direction: column; gap: 0.15rem; flex: 1; min-width: 62px; }
.pf span { font-size: 0.68rem; font-weight: 600; color: var(--gym-text-muted); }
.pf input {
  width: 100%; min-width: 0; padding: 0.45rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm);
  font-size: 1rem; background: var(--gym-surface); color: var(--gym-text); -moz-appearance: textfield;
}
.pf input::-webkit-outer-spin-button, .pf input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.pf input:focus { outline: none; border-color: var(--gym-accent); box-shadow: 0 0 0 3px var(--gym-accent-soft); }
.pf-sm { max-width: 62px; }
.pf-time { flex: 2; min-width: 130px; }
.time-row { display: flex; gap: 0.3rem; }
.time-row input { flex: 1; }
.sw-btn {
  background: var(--gym-accent-soft); border: 1px solid var(--gym-accent-border); color: var(--gym-accent);
  border-radius: var(--gym-radius-sm); padding: 0 0.55rem; font-size: 0.8rem; cursor: pointer; white-space: nowrap; font-variant-numeric: tabular-nums;
}
.sw-btn.running { background: var(--gym-danger-soft); border-color: var(--gym-danger); color: var(--gym-danger); }
.planned-actions { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.warmup-toggle { font-size: 0.78rem; color: var(--gym-text-muted); display: flex; align-items: center; gap: 0.3rem; cursor: pointer; }
.warmup-toggle input { accent-color: var(--gym-accent); }
.set-note-btn { background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); cursor: pointer; font-size: 0.8rem; padding: 0.3rem 0.45rem; color: var(--gym-text-muted); }
.set-note-btn.active { background: var(--gym-accent-soft); border-color: var(--gym-accent-border); }
.set-del-open { background: none; border: none; cursor: pointer; font-size: 0.9rem; color: var(--gym-text-faint); }
.set-del-open:hover { color: var(--gym-danger); }
.btn-done {
  margin-left: auto; background: var(--gym-success); border: none; color: #06281a;
  border-radius: var(--gym-radius-sm); padding: 0.5rem 1rem; font-size: 0.88rem; font-weight: 800; cursor: pointer;
  transition: filter var(--gym-dur) var(--gym-ease);
}
.btn-done:hover { filter: brightness(1.06); }
.set-note-input {
  width: 100%; padding: 0.45rem 0.55rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm);
  font-size: 0.85rem; background: var(--gym-surface); color: var(--gym-text);
}

.btn-add-set {
  background: none; border: 1.5px dashed var(--gym-accent-border); color: var(--gym-accent);
  border-radius: var(--gym-radius-sm); padding: 0.55rem; font-size: 0.88rem; font-weight: 600; cursor: pointer; width: 100%;
}
.btn-add-set:hover { background: var(--gym-accent-soft); }

/* Add exercise */
.btn-add-exercise {
  width: 100%; background: var(--gym-surface); border: 1.5px dashed var(--gym-accent-border); color: var(--gym-accent);
  border-radius: var(--gym-radius); padding: 0.9rem; font-size: 0.95rem; font-weight: 700; cursor: pointer;
}
.btn-add-exercise:hover { background: var(--gym-accent-soft); }
.exercise-picker {
  background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius); padding: 0.9rem;
  display: flex; flex-direction: column; gap: 0.6rem; box-shadow: var(--gym-shadow-sm);
}
.picker-search { padding: 0.65rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); font-size: 0.95rem; background: var(--gym-surface-2); color: var(--gym-text); }
.picker-search:focus { outline: none; border-color: var(--gym-accent); box-shadow: 0 0 0 3px var(--gym-accent-soft); }
.picker-list { display: flex; flex-direction: column; gap: 0.3rem; max-height: 260px; overflow-y: auto; }
.picker-item {
  display: flex; justify-content: space-between; align-items: center; gap: 0.5rem;
  background: var(--gym-surface-2); border: none; border-radius: var(--gym-radius-sm); padding: 0.65rem 0.75rem;
  font-size: 0.92rem; color: var(--gym-text); cursor: pointer; text-align: left;
}
.picker-item:hover { background: var(--gym-accent-soft); }
.picker-tags { font-size: 0.7rem; color: var(--gym-text-faint); }
.picker-empty { color: var(--gym-text-faint); font-size: 0.85rem; padding: 0.5rem; }
.picker-close { background: none; border: none; color: var(--gym-text-muted); cursor: pointer; font-size: 0.85rem; }

/* transitions */
.slide-enter-active, .slide-leave-active { transition: opacity 0.2s, transform 0.2s; }
.slide-enter-from, .slide-leave-to { opacity: 0; transform: translateY(-6px); }
.pop-enter-active { transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), opacity 0.25s; }
.pop-enter-from { transform: translateX(-50%) scale(0.7); opacity: 0; }
.pop-leave-active { transition: opacity 0.3s; }
.pop-leave-to { opacity: 0; }
.expand-enter-active, .expand-leave-active { transition: opacity 0.2s, transform 0.2s; }
.expand-enter-from, .expand-leave-to { opacity: 0; transform: translateY(-4px); }
.set-flag-mark { font-size: 0.85em; margin-left: 0.25rem; }
</style>
